import { useEffect, useState } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

let toastId = 0;
const generateId = () => `toast-${++toastId}`;

// Simple toast store for use outside React components
const toastStore: {
  listeners: Array<(toasts: Toast[]) => void>;
  toasts: Toast[];
  addToast: (message: string, type?: ToastType) => void;
  removeToast: (id: string) => void;
} = {
  listeners: [],
  toasts: [],
  addToast(message: string, type: ToastType = 'info') {
    const toast: Toast = { id: generateId(), message, type };
    this.toasts = [...this.toasts, toast];
    this.listeners.forEach((listener) => listener(this.toasts));

    // Auto-remove after 5 seconds
    setTimeout(() => {
      this.removeToast(toast.id);
    }, 5000);
  },
  removeToast(id: string) {
    this.toasts = this.toasts.filter((t) => t.id !== id);
    this.listeners.forEach((listener) => listener(this.toasts));
  },
};

export const toast = {
  success: (message: string) => toastStore.addToast(message, 'success'),
  error: (message: string) => toastStore.addToast(message, 'error'),
  warning: (message: string) => toastStore.addToast(message, 'warning'),
  info: (message: string) => toastStore.addToast(message, 'info'),
};

export function ToastContainer() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const listener = (newToasts: Toast[]) => setToasts(newToasts);
    toastStore.listeners.push(listener);
    return () => {
      toastStore.listeners = toastStore.listeners.filter((l) => l !== listener);
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="toast-container">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast-${t.type}`}>
          <span className="toast-message">{t.message}</span>
          <button className="toast-close" onClick={() => toastStore.removeToast(t.id)}>
            &times;
          </button>
        </div>
      ))}
    </div>
  );
}
